import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Car, MapPin, ChevronRight, MessageCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useMediaQuery } from "@/hooks/use-media-query";
import { imageAtWidth, imageSrcSet } from "@/lib/images";
import { ItineraryTimeline } from "@/components/site/ItineraryTimeline";
import { HotelsPricingMatrix } from "@/components/site/HotelsPricingMatrix";
import { InclusionsExclusions } from "@/components/site/InclusionsExclusions";
import { PoliciesTab } from "@/components/site/PoliciesTab";
import { EnquiryWidget } from "@/components/site/EnquiryWidget";
import { getTour } from "@/lib/api";
import { formatINR } from "@/lib/format";

export default function TourDetail() {
  const { slug } = useParams();
  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    getTour(slug)
      .then(setTour)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <Skeleton className="h-64 w-full rounded-xl" />
        <div className="mt-6 grid grid-cols-12 gap-6">
          <Skeleton className="col-span-12 h-96 rounded-xl lg:col-span-8" />
          <Skeleton className="col-span-12 h-96 rounded-xl lg:col-span-4" />
        </div>
      </div>
    );
  }

  if (notFound || !tour) {
    return (
      <div className="mx-auto flex w-full max-w-7xl flex-col items-center px-4 py-20 text-center">
        <h2 className="font-display text-2xl font-semibold text-slate-900">Package not found</h2>
        <p className="mt-2 text-slate-500">This tour package may have been removed or the link is incorrect.</p>
        <Link to="/packages" className="mt-6">
          <Button className="bg-[hsl(var(--brand-primary))]">Browse All Packages</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="tour-detail-page pb-24 lg:pb-10">
      {/* Header banner */}
      <div className="tour-detail-banner relative h-64 w-full overflow-hidden sm:h-80">
        <img src={imageAtWidth(tour.cover_image, 1280)} srcSet={imageSrcSet(tour.cover_image, [640, 960, 1280, 1920])} sizes="100vw" width="1920" height="800" fetchPriority="high" alt={tour.tour_title} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/20 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 mx-auto w-full max-w-7xl px-4 pb-5 sm:px-6 lg:px-8">
          <Link to="/packages" data-testid="tour-detail-back-link" className="mb-3 inline-flex items-center gap-1 text-sm text-white/90 hover:text-white">
            <ArrowLeft className="h-4 w-4" /> Back to Packages
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            <Badge className="bg-[hsl(var(--brand-accent))] text-slate-900">{tour.region_tag}</Badge>
            <Badge variant="outline" className="border-white/40 bg-white/10 text-white">
              {tour.duration.nights} Nights / {tour.duration.days} Days
            </Badge>
          </div>
          <h1 className="mt-2 max-w-3xl font-display text-2xl font-bold text-white sm:text-3xl lg:text-4xl">{tour.tour_title}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-white/90">
            <Car className="h-4 w-4" /> {tour.vehicle_type}
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-7xl px-4 pt-6 sm:px-6 lg:px-8">
        {/* Routing pills */}
        <div data-testid="tour-routing" className="mb-6 flex flex-wrap gap-2">
          {tour.routing.map((city, idx) => (
            <span key={idx} data-testid={`tour-routing-stop-${idx}`} className="route-pill shrink-0">
              <MapPin className="mr-1 h-3 w-3" />
              {city}
              {idx < tour.routing.length - 1 && <ChevronRight className="ml-1 h-3 w-3" />}
            </span>
          ))}
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Left main content */}
          <div className="col-span-12 min-w-0 lg:col-span-8">
            <Tabs defaultValue="itinerary" data-testid="tour-detail-tabs">
              <TabsList className="tour-tabs grid h-auto w-full grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1 md:flex md:justify-start">
                <TabsTrigger value="itinerary" data-testid="tour-tab-itinerary" className="data-[state=active]:bg-white">Itinerary</TabsTrigger>
                <TabsTrigger value="hotels" data-testid="tour-tab-hotels-pricing" className="data-[state=active]:bg-white">Hotels & Pricing</TabsTrigger>
                <TabsTrigger value="inclusions" data-testid="tour-tab-inclusions-exclusions" className="data-[state=active]:bg-white">Inclusions & Exclusions</TabsTrigger>
                <TabsTrigger value="policies" data-testid="tour-tab-policies" className="data-[state=active]:bg-white">Policies & Fine Print</TabsTrigger>
              </TabsList>

              <TabsContent value="itinerary" className="mt-6 rounded-xl border bg-white p-5">
                <ItineraryTimeline itinerary={tour.itinerary} />
              </TabsContent>
              <TabsContent value="hotels" className="mt-6 rounded-xl border bg-white p-5">
                <HotelsPricingMatrix tour={tour} />
              </TabsContent>
              <TabsContent value="inclusions" className="mt-6 rounded-xl border bg-white p-5">
                <InclusionsExclusions inclusions={tour.inclusions} exclusions={tour.exclusions} />
              </TabsContent>
              <TabsContent value="policies" className="mt-6 rounded-xl border bg-white p-5">
                <PoliciesTab tour={tour} />
              </TabsContent>
            </Tabs>
          </div>

          {/* Right sticky sidebar - desktop only */}
          {isDesktop && <div className="col-span-4 min-w-0">
            <div className="sticky top-20">
              <EnquiryWidget key={tour.id} tour={tour} />
            </div>
          </div>}
        </div>
      </div>

      {/* Mobile bottom bar */}
      {!isDesktop && <div data-testid="mobile-booking-bar" className="mobile-booking-bar fixed inset-x-0 bottom-0 z-40 flex items-center justify-between gap-4 border-t bg-card px-4 py-3 shadow-soft">
        <div className="mobile-booking-price min-w-0">
          <p className="text-xs text-muted-foreground">Starting price / person</p>
          <p data-testid="mobile-booking-price" className="font-display text-lg font-bold text-foreground">
            {tour.price_mode === "fixed" ? formatINR(tour.starting_price) : "On Request"}
          </p>
        </div>
        <Button asChild variant="outline" size="icon" className="h-12 w-12 shrink-0 border-primary text-primary" data-testid="mobile-booking-whatsapp">
          <a href={`https://wa.me/919816672006?text=${encodeURIComponent(`Hi, I'd like to know more about ${tour.tour_title}.`)}`} aria-label="Ask about this trip on WhatsApp" target="_blank" rel="noopener noreferrer"><MessageCircle className="h-5 w-5" /></a>
        </Button>
        <Sheet open={mobileSheetOpen} onOpenChange={setMobileSheetOpen}>
          <SheetTrigger asChild>
            <Button data-testid="mobile-enquire-cta" className="min-h-12 bg-accent px-6 text-base font-semibold text-accent-foreground hover:bg-accent/90">Enquire Now</Button>
          </SheetTrigger>
          <SheetContent side="bottom" data-testid="enquiry-drawer" closeTestId="enquiry-drawer-close-button" closeLabel="Close enquiry" className="mobile-enquiry-sheet flex flex-col gap-0 overflow-hidden rounded-t-2xl p-0">
            <div className="shrink-0 border-b px-4 py-5 pr-16">
              <SheetTitle data-testid="enquiry-drawer-title" className="font-sans">Plan this escape</SheetTitle>
              <SheetDescription data-testid="enquiry-drawer-description" className="mt-1 line-clamp-2">{tour.tour_title}</SheetDescription>
            </div>
            <div className="mobile-enquiry-body min-h-0 flex-1 overflow-y-auto overscroll-contain" data-testid="mobile-open-enquiry-sheet">
              <EnquiryWidget key={tour.id} tour={tour} mobile />
            </div>
          </SheetContent>
        </Sheet>
      </div>}
    </div>
  );
}
